import apiClient from '@/lib/api';

// Admin service for the Onboarding section (v2 book-a-call flow).

export interface AdminOnboardingCallNote {
  id: string; // publicId
  body: string;
  origin: 'backoffice' | 'ghl';
  authorName: string | null;
  dateAdded: string;
  ghlSynced: boolean;
}

export interface AdminOnboardingCallRecord {
  id: string; // publicId
  outcome: 'booked' | 'skipped';
  source: string;
  sourceUrl: string | null;
  device: string | null;
  deviceBrand: string | null;
  browser: string | null;
  userAgent: string | null;
  planAtDecision: string | null;
  startAtUtc: string | null;
  endAtUtc: string | null;
  timezone: string | null;
  assignedUserName: string | null;
  assignedUserPhotoUrl: string | null;
  status: string;
  /** Notes 2-way synced with the GHL appointment's Notes tab, newest first. */
  notes: AdminOnboardingCallNote[];
  createdAtUtc: string;
  ghlEventId: string | null;
  /** Set when the last background removal of this row failed. */
  removalError: string | null;
  agency: { id: string; name: string } | null;
  contact: { name: string | null; email: string | null } | null;
}

export interface OnboardingCallListMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CallMutationResult {
  record: AdminOnboardingCallRecord;
  ghlSynced: boolean;
}

export const onboardingService = {
  async listCalls(params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    outcome?: string;
    source?: string;
    device?: string;
    /** YYYY-MM-DD bounds on the booked-for time (either end optional). */
    bookedFrom?: string;
    bookedTo?: string;
    sortBy?: 'startAtUtc' | 'createdAtUtc';
    sortDir?: 'asc' | 'desc';
  }): Promise<{ data: AdminOnboardingCallRecord[]; meta: OnboardingCallListMeta }> {
    const query = new URLSearchParams();
    query.set('page', String(params.page ?? 1));
    query.set('limit', String(params.limit ?? 20));
    if (params.search) query.set('search', params.search);
    if (params.status) query.set('status', params.status);
    if (params.outcome) query.set('outcome', params.outcome);
    if (params.source) query.set('source', params.source);
    if (params.device) query.set('device', params.device);
    if (params.bookedFrom) query.set('bookedFrom', params.bookedFrom);
    if (params.bookedTo) query.set('bookedTo', params.bookedTo);
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortDir) query.set('sortDir', params.sortDir);
    const res = await apiClient.get(`/admin/onboarding/calls?${query.toString()}`);
    return res.data;
  },

  async updateCall(id: string, patch: { status?: string }): Promise<CallMutationResult> {
    const res = await apiClient.patch(`/admin/onboarding/calls/${id}`, patch);
    return { record: res.data.data, ghlSynced: res.data.ghlSynced !== false };
  },

  async addNote(callId: string, body: string): Promise<CallMutationResult> {
    const res = await apiClient.post(`/admin/onboarding/calls/${callId}/notes`, { body });
    return { record: res.data.data, ghlSynced: res.data.ghlSynced !== false };
  },

  async updateNote(callId: string, noteId: string, body: string): Promise<CallMutationResult> {
    const res = await apiClient.patch(`/admin/onboarding/calls/${callId}/notes/${noteId}`, { body });
    return { record: res.data.data, ghlSynced: res.data.ghlSynced !== false };
  },

  async deleteNote(callId: string, noteId: string): Promise<CallMutationResult> {
    const res = await apiClient.delete(`/admin/onboarding/calls/${callId}/notes/${noteId}`);
    return { record: res.data.data, ghlSynced: res.data.ghlSynced !== false };
  },

  async removeCalls(ids: string[]): Promise<{ queued: number }> {
    const res = await apiClient.post('/admin/onboarding/calls/remove', { ids });
    return res.data.data;
  },
};
